import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const baseSvg = (size: number, rest: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
  ...rest,
});

export function SearchIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={11} cy={11} r={7} />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function PlusIcon({ size = 18, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function ListIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx={3.5} cy={6} r={1} fill="currentColor" stroke="none" />
      <circle cx={3.5} cy={12} r={1} fill="currentColor" stroke="none" />
      <circle cx={3.5} cy={18} r={1} fill="currentColor" stroke="none" />
    </svg>
  );
}

export function GridIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <rect x={3} y={3} width={7} height={7} rx={1.5} />
      <rect x={14} y={3} width={7} height={7} rx={1.5} />
      <rect x={3} y={14} width={7} height={7} rx={1.5} />
      <rect x={14} y={14} width={7} height={7} rx={1.5} />
    </svg>
  );
}

export function SortIcon({ size = 12, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="m7 9 5-5 5 5M7 15l5 5 5-5" />
    </svg>
  );
}

export function SortAscIcon({ size = 12, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="m7 14 5-5 5 5" />
    </svg>
  );
}

export function SortDescIcon({ size = 12, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="m7 10 5 5 5-5" />
    </svg>
  );
}

export function MoreIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={5} cy={12} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={12} cy={12} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={19} cy={12} r={1.4} fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ChevronLeftIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 14, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function InfoIcon({ size = 14, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={12} r={9} />
      <path d="M12 8h.01M11 12h1v5h1" />
    </svg>
  );
}

export function SettingsIcon({ size = 18, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={12} r={3} />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.3a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.7 15a1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.7 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.7a1.7 1.7 0 0 0 1-1.6V3a2 2 0 1 1 4 0v.1A1.7 1.7 0 0 0 15 4.7a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

export function CameraIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M3 7a2 2 0 0 1 2-2h2l1.5-2h7L17 5h2a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <circle cx={12} cy={13} r={3.5} />
    </svg>
  );
}

export function AlertIcon({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M12 9v4M12 17h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.74 3h16.88a2 2 0 0 0 1.74-3L13.7 3.86a2 2 0 0 0-3.4 0Z" />
    </svg>
  );
}

export function InboxIcon({ size = 24, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}
